import { financingStepOptions, PrepItem } from '@bariendo/lib/payment-status';
import { Track } from '@bariendo/lib/constants';
import { Box } from '@mantine/core';
import { getReferenceString, resolveId } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useInsurerList } from '@/hooks/useInsurerList';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { FINANCING_PATIENTS_QUERY } from '../../graphql';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { usePrepItems } from '../../hooks/usePrepItems';
import { QUEUE_PAGE_ROWS, useQueuePage } from '../../hooks/useQueuePage';
import { coverageTabValues } from '../../services/coverageFilters';
import { buildCoverageRows, CoverageRow } from '../../services/coverageRows';
import { readyToScheduleSubscriptionParams } from '../../services/readyToScheduleScope';
import { buildInsuranceFilter, matchesInsuranceTab } from '../../services/insuranceFilters';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  isQueueFiltered,
  LEAD_STATUS_FILTER_OPTIONS,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterOption,
  QueueFilterValues,
} from '../../services/queueFilters';
import { paymentStatusColumn } from '../../intake/tabs/financingColumns';
import { patientColumn, payorColumn, pnConsultationColumn } from '../../intake/tabs/hubColumns';
import { notesColumn } from '../../intake/tabs/notesColumn';
import { statusColumn } from '../../intake/tabs/statusColumn';
import { EditedRow, useHubRowEdits } from '../../intake/tabs/useHubRowEdits';
import { prepChecklistColumn } from './prepChecklistColumn';

/**
 * The Coverage pill's Insurance tab (BAR-3098): care ops' list of insurance patients in Financing
 * & approval, with the prep checklist the status leaves out. Membership and the filters run on the
 * server on the stage, track, payor and payment status tags.
 */

const DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, status: 'active' };

const STEP_FILTER_OPTIONS = [{ value: QUEUE_FILTER_ANY, label: 'All' }, ...financingStepOptions([Track.INSURANCE])];

/** Case notes are the hub's longest (`intake-hub.mdx`). */
const NOTES_FLOOR = 320;

type InsuranceRow = CoverageRow & { prepItems?: PrepItem[] };
type HubInsuranceRow = EditedRow<InsuranceRow>;

const HYDRATION_NEEDS = {
  consult: true,
  procedure: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface InsuranceTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function InsuranceTab({ now }: InsuranceTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);
  const { canViewPayments } = useCurrentUser().currentUserPermissions;

  const { values, setValues, setFilter, defaults } = useHubFilterValues(DEFAULTS);
  const filter = useMemo(() => buildInsuranceFilter(values), [values]);
  const subscriptionParams = useMemo(() => readyToScheduleSubscriptionParams(), []);

  const {
    rows: data,
    total,
    loading,
    error,
    page,
    setPage,
  } = useQueuePage<Patient>(FINANCING_PATIENTS_QUERY, 'Patient', filter, subscriptionParams);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the insurance queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const patients = useMemo(() => (error ? [] : data), [data, error]);
  const patientRefs = useMemo(
    () =>
      patients
        .map((patient) => (patient.id ? getReferenceString(patient) : undefined))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: lastCommunicationFloor(today) },
    `coverage-insurance:${filter}`
  );
  const prep = usePrepItems(patientRefs);

  const builtRows = useMemo(
    () =>
      buildCoverageRows(patients, hydration, resolvedNow, canViewPayments).map(
        (row): InsuranceRow => ({
          ...row,
          prepItems: prep.itemsByPatient.get(getReferenceString(row.patient) ?? ''),
        })
      ),
    [patients, hydration, resolvedNow, canViewPayments, prep.itemsByPatient]
  );
  const edits = useHubRowEdits(builtRows, values.status);
  const { rows } = edits;

  // Rows that arrive by subscription skip the query's filter, so they are checked on their tags.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesInsuranceTab(row.patient, values) &&
          matchesPatientFilterTags(row.patient, { ...coverageTabValues(values), status: QUEUE_FILTER_ANY }) &&
          (values.status === QUEUE_FILTER_ANY || row.activeLead.status === values.status)
      ),
    [rows, values]
  );
  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);
  const insurers = useInsurerList({ enabled: true });
  const insurerOptions = useMemo<QueueFilterOption[]>(
    () => [
      { value: QUEUE_FILTER_ANY, label: 'All' },
      ...insurers.options.map((option) => ({
        value: resolveId({ reference: option.reference }) ?? '',
        label: option.name,
      })),
    ],
    [insurers.options]
  );

  const { onNoteSaved, changeStatus, undoStatus } = edits;
  const columns = useMemo(
    () => [
      patientColumn<HubInsuranceRow>(),
      payorColumn<HubInsuranceRow>('Insurer'),
      pnConsultationColumn<HubInsuranceRow>(),
      prepChecklistColumn<HubInsuranceRow>(),
      paymentStatusColumn<HubInsuranceRow>(),
      notesColumn<HubInsuranceRow>({ onSaved: onNoteSaved, minSize: NOTES_FLOOR }),
      statusColumn<HubInsuranceRow>({ onChange: changeStatus, onUndo: undoStatus, header: 'Lead status' }),
    ],
    [onNoteSaved, changeStatus, undoStatus]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={null}
        filters={[
          { label: 'Insurer', value: values.payor, options: insurerOptions, onChange: setFilter('payor') },
          { label: 'Step', value: values.step, options: STEP_FILTER_OPTIONS, onChange: setFilter('step') },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          { label: 'Status', value: values.status, options: LEAD_STATUS_FILTER_OPTIONS, onChange: setFilter('status') },
        ]}
        isFiltered={isQueueFiltered(coverageTabValues(values), coverageTabValues(defaults))}
        onReset={() => setValues(defaults)}
      />
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading || prep.loading}
        initialSorting={[{ id: 'pnConsultation', desc: false }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        getRowClassName={edits.rowClassName}
      />
      <QueueFooter
        shown={visibleRows.length}
        total={total}
        page={page}
        pageCount={Math.ceil((total ?? 0) / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
